import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  Inject,
  inject,
  signal,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Params, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { AvatarDirective } from 'src/app/common/avatar/avatar.directive';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { RouteTranslatePipe } from 'src/app/_shared/pipes/route-translate.pipe';
import { User } from 'src/types/graphql';
import { HelperService } from 'utils/helper';
import { SearchBarMobileComponent } from '../search-bar/search-bar-mobile/search-bar-mobile.component';
import { SearchBarStore } from '../search-bar/search-bar.store';
import { UserService } from '../../../common/user/user.service';
import { BottomBarFilterActiveService } from './bottom-bar-filter-active/bottom-bar-filter-active.service';
import { PostDialogsService } from '../../services/post/post-dialogs.service';

@Component({
  selector: 'sharry-bottom-bar',
  templateUrl: './bottom-bar.component.html',
  styleUrls: ['./bottom-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouteTranslatePipe,
    I18NextModule,
    RouterLink,
    AvatarDirective,
    IconComponent,
    SearchBarMobileComponent,
    MatButtonModule,
    RouterLinkActive,
  ],
})
export class BottomBarComponent extends BaseComponent {
  user = signal<Partial<User> | null>(null);
  activeList: boolean[] = [];
  private searchBarStore = inject(SearchBarStore);
  private postDialogs = inject(PostDialogsService);
  readonly overSearch = computed(() => this.searchBarStore.isOpen() && this.searchBarStore.isMobile());
  readonly keyboardOpen = this.searchBarStore.keyboardOpen;

  onTouchMove(event: TouchEvent) {
    if (this.overSearch() && event.cancelable) event.preventDefault();
  }

  isRouteActive(button: BottomBarButton, routeActive: boolean): boolean {
    return !!button.link?.length && routeActive && !this.searchBarStore.isOpen();
  }

  constructor(
    private userService: UserService,
    public bottomBarFilterActive: BottomBarFilterActiveService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private cd: ChangeDetectorRef,
    private bottomSheet: MatBottomSheet,
    private dialog: MatDialog,
    private helperService: HelperService,
    private router: Router,
  ) {
    super();

    effect(
      () => {
        if (!this.searchBarStore.isOpen()) this.bottomBarFilterActive.deactivate(1);
      },
      { allowSignalWrites: true },
    );

    this.translateService.events.languageChanged.subscribe(() => {
      this.bottomBarFilterActive.bottomBarButtons.set(this.buildButtons(this.user()));
      this.cd.markForCheck();
    });

    this.userService.user$.subscribe(u => {
      this.user.set(u);
      this.bottomBarFilterActive.bottomBarButtons.set(this.buildButtons(u));
    });

    this.subscriptions.push(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        this.bottomBarFilterActive.bottomBarButtons.update(list =>
          list.map((btn, i) => (i === 1 || btn.link?.length ? { ...btn, active: false } : btn)),
        );
      }),
    );
  }

  private buildButtons(user: Partial<User> | null): BottomBarButton[] {
    const buttons: BottomBarButton[] = [
      {
        text: this.translateService.t('explore.bottom-bar.explore'),
        icon: 'house',
        link: 'explore',
        class: 'sharry-small',
        color: 'secondary',
        active: false,
      },
      {
        text: this.translateService.t('explore.bottom-bar.search'),
        icon: 'magnifying-glass',
        link: null,
        class: 'sharry-small',
        color: 'secondary',
        active: false,
        onClick: () => this.searchBarStore.open(),
      },
      {
        text: this.translateService.t('header.create'),
        icon: 'ico-community',
        link: null,
        class: 'sharry-small',
        color: 'secondary',
        active: false,
        onClick: () => {
          this.bottomBarFilterActive.deactivate(2);
          this.postDialogs.openCreate();
        },
      },
      {
        text: this.translateService.t('header.likes-short'),
        icon: 'preferiti-mobile',
        link: 'likes',
        class: 'sharry-small',
        color: 'secondary',
        active: false,
      },
    ];

    if (!user) {
      buttons.push({
        text: this.translateService.t('explore.bottom-bar.sign-up'),
        icon: 'user-circle',
        link: null,
        class: 'sharry-small',
        color: 'secondary',
        onClick: () => this.openMobileMenu(),
      });
    } else {
      buttons.push({
        text: '',
        icon: '',
        link: null,
        class: 'sharry-big',
        color: 'secondary',
        avatar: true,
        onClick: () => this.openMobileMenu(),
      });
    }

    return buttons;
  }

  openMobileMenu(): void {
    if (this.helperService.isMobileIpad()) {
      this.openIpadMenu();
    } else {
      import('../header-menu/header-menu-mobile/header-menu-mobile.component').then(({ HeaderMenuMobileComponent }) =>
        this.bottomSheet
          .open(HeaderMenuMobileComponent, {
            panelClass: 'mobile-menu-sheet',
          })
          .afterDismissed()
          .subscribe(() => this.deactivateMenu()),
      );
    }
  }

  private async openIpadMenu(): Promise<void> {
    const { HeaderMenuIpadDialogComponent } = await import(
      '../header-menu/header-menu-ipad-dialog/header-menu-ipad-dialog.component'
    );
    const isLoggedIn = !!this.user();
    const menuRef = this.dialog.open(HeaderMenuIpadDialogComponent, {
      panelClass: 'ipad-menu-dialog',
      position: { bottom: '80px', right: '16px' },
      autoFocus: false,
      restoreFocus: false,
      data: { isLoggedIn },
    });
    menuRef.componentInstance.isLoggedIn = isLoggedIn;
    menuRef.afterClosed().subscribe(() => this.deactivateMenu());

    menuRef.componentInstance.closeMenu.subscribe(() => menuRef.close());

    if (!isLoggedIn) {
      menuRef.componentInstance.openAuth.subscribe((params: Params) => {
        menuRef.close();
        if (params['signup']) {
          import('../../dialogs/sign-up/sign-up.component').then(({ SignUpComponent }) =>
            this.dialog.open(SignUpComponent, { autoFocus: false, restoreFocus: false }),
          );
        } else if (params['signin']) {
          import('../../dialogs/sign-in/sign-in.component').then(({ SignInComponent }) =>
            this.dialog.open(SignInComponent, { autoFocus: false, restoreFocus: false }),
          );
        }
      });
    }
  }

  private deactivateMenu() {
    this.bottomBarFilterActive.deactivate(this.bottomBarFilterActive.bottomBarButtons().length - 1);
  }

  onActiveChange(index: number) {
    this.bottomBarFilterActive.bottomBarButtons.update(list =>
      list.map((btn, i) => ({
        ...btn,
        active: i === index,
      })),
    );
    this.activeList = this.activeList.map(() => false);
    this.activeList[index] = true;
    if (index !== 1) this.searchBarStore.close();
  }
}

export interface BottomBarButton {
  text: string;
  icon: string;
  link: string | string[] | null;
  class: string;
  color: string;
  queryParams?: Params;
  avatar?: boolean;
  onClick?: () => void;
  active?: boolean;
}
